import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerScope, switchScopeTarget } from '../lib/navigation.js';
import { useFocusTrap } from '../lib/use-focus-trap.js';

/** The most results Quick find ever lists. */
export const QUICK_FIND_LIMIT = 8;
// Sections keep at least this many rows when they match, so a broad query
// that hits many customers cannot push every section out of the list.
const SECTION_RESERVE = 3;

const includes = (text, q) => String(text ?? '').toLowerCase().includes(q);

/**
 * The Quick find results for a query: customers (name or id) first, then the
 * sections of the active navigation (label, or a keyword such as an old page name), each a case-insensitive
 * substring match. Pure, client-side. An empty query lists everything up to
 * the limit. Sections are the same items the sidebar shows (lib/navigation.js
 * navigationFor), so the two never drift.
 *
 * @returns {{ customers: object[], sections: object[] }} at most `limit` rows together
 */
export function quickFindResults({ options = [], groups = [], query = '', limit = QUICK_FIND_LIMIT }) {
  const q = String(query ?? '').trim().toLowerCase();
  const customers = options.filter((o) => !q || includes(o.name, q) || includes(o.customerId, q));
  const sections = groups.flatMap((g) => g.items
    .filter((i) => !q || includes(i.label, q) || (i.keywords ?? []).some((k) => includes(k, q)))
    .map((i) => ({ id: i.id, label: i.label, to: i.to, group: g.label })));
  const customerCap = limit - Math.min(sections.length, SECTION_RESERVE);
  const shownCustomers = customers.slice(0, customerCap);
  return { customers: shownCustomers, sections: sections.slice(0, limit - shownCustomers.length) };
}

/** The text with the first (case-insensitive) match of the query in a mark. */
export function Highlight({ text, query }) {
  const value = String(text ?? '');
  const q = String(query ?? '').trim().toLowerCase();
  const lower = value.toLowerCase();
  const at = q && lower.length === value.length ? lower.indexOf(q) : -1;
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at)}
      <mark className="bg-transparent font-bold text-ipc-primary underline">{value.slice(at, at + q.length)}</mark>
      {value.slice(at + q.length)}
    </>
  );
}

function shortcutHint() {
  const platform = typeof navigator === 'undefined' ? '' : `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`;
  return /Mac|iPhone|iPad/.test(platform) ? 'Cmd K' : 'Ctrl K';
}

/**
 * Quick find (ENTRAID-290): a search field in the TopBar that opens a
 * dialog with one input and a results list, from a click or Cmd+K / Ctrl+K
 * anywhere; Escape closes it and focus goes back to the trigger. It matches
 * the customers this user can already switch to and the sections of the
 * active navigation. Fully client-side: it reads what the shell already
 * loaded and adds no request. Display only: every route still hits the
 * backend checks.
 */
export default function QuickFind({ navClaims, scope, pathname, options = [], groups = [] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();
  const optionId = (i) => `${listId}-option-${i}`;

  // Cmd+K / Ctrl+K anywhere, including inside other inputs.
  useEffect(() => {
    function onKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  function close() {
    setOpen(false);
    setQuery('');
    setActive(0);
  }

  useFocusTrap(panelRef, { active: open, onClose: close, returnFocusRef: triggerRef, initialFocusRef: inputRef, closeOnOutsidePointer: false });

  const results = useMemo(() => quickFindResults({ options, groups, query }), [options, groups, query]);
  const flat = useMemo(() => [
    ...results.customers.map((c) => ({ kind: 'customer', key: `customer:${c.customerId}`, data: c })),
    ...results.sections.map((s) => ({ kind: 'section', key: `section:${s.id}`, data: s })),
  ], [results]);
  const activeIndex = flat.length === 0 ? -1 : Math.min(active, flat.length - 1);

  function choose(entry) {
    const to = entry.kind === 'customer'
      ? switchScopeTarget(navClaims, pathname, customerScope(entry.data.customerId))
      : entry.data.to;
    close();
    navigate(to);
  }

  function onKeyDown(event) {
    if (event.nativeEvent?.isComposing) return;
    if (event.key === 'ArrowDown' && flat.length > 0) {
      event.preventDefault();
      setActive((activeIndex + 1) % flat.length);
    } else if (event.key === 'ArrowUp' && flat.length > 0) {
      event.preventDefault();
      setActive((activeIndex - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      choose(flat[activeIndex]);
    }
  }

  const heading = 'px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-600';
  const rowBase = 'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm';
  const groupsToShow = [
    { id: 'customers', label: 'Customers', entries: flat.filter((e) => e.kind === 'customer') },
    { id: 'sections', label: 'Sections', entries: flat.filter((e) => e.kind === 'section') },
  ].filter((g) => g.entries.length > 0);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Quick find"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="tap flex items-center justify-center gap-2 rounded-md px-2 py-1 text-sm text-white hover:bg-white/10 md:w-52 md:bg-white/10 md:py-1.5 md:hover:bg-white/20"
      >
        <svg aria-hidden="true" className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <span aria-hidden="true" className="hidden min-w-0 flex-1 truncate text-left md:inline">Quick find</span>
        <kbd aria-hidden="true" className="hidden rounded border border-white/40 px-1 font-sans text-xs md:inline">{shortcutHint()}</kbd>
      </button>
      {open && (
        <div className="fixed inset-0 z-50">
          <div data-testid="quick-find-backdrop" aria-hidden="true" className="absolute inset-0 bg-slate-900/40" onClick={close} />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Quick find"
            className="on-light relative mx-auto mt-16 w-[calc(100%-2rem)] max-w-lg rounded-md border border-ipc-border bg-white p-2 text-ipc-text shadow-lg"
          >
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-label="Quick find"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
              autoComplete="off"
              spellCheck="false"
              placeholder="Quick find"
              className="input text-sm"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
            />
            <div id={listId} role="listbox" aria-label="Results" className="mt-2 max-h-80 overflow-y-auto">
              {groupsToShow.map((g) => (
                <div key={g.id} role="group" aria-labelledby={`${listId}-${g.id}`}>
                  <div id={`${listId}-${g.id}`} role="presentation" className={heading}>{g.label}</div>
                  {g.entries.map((entry) => {
                    const index = flat.indexOf(entry);
                    const isActive = index === activeIndex;
                    return (
                      <div
                        key={entry.key}
                        id={optionId(index)}
                        role="option"
                        aria-selected={isActive}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseMove={() => setActive(index)}
                        onClick={() => choose(entry)}
                        className={`${rowBase} ${isActive ? 'bg-slate-100' : ''}`}
                      >
                        {entry.kind === 'customer' ? (
                          <Fragment>
                            <span data-option-name className="min-w-0 flex-1 truncate font-medium"><Highlight text={entry.data.name} query={query} /></span>
                            <span className="min-w-0 truncate font-mono text-xs text-slate-600"><Highlight text={entry.data.customerId} query={query} /></span>
                          </Fragment>
                        ) : (
                          <Fragment>
                            <span data-option-name className="min-w-0 flex-1 truncate font-medium"><Highlight text={entry.data.label} query={query} /></span>
                            <span className="text-xs text-slate-600">{entry.data.group}</span>
                          </Fragment>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {flat.length === 0 && <p role="status" className="px-2 py-2 text-sm text-slate-600">No matches</p>}
          </div>
        </div>
      )}
    </>
  );
}
